import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';
import {useSnackbar} from 'notistack';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Add, Delete, Edit, Refresh} from '@mui/icons-material';
import dayjs from 'dayjs';
import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {GetFileTypeData, ProductExampleStatus, ProductExampleSummaryDto} from 'apiClient/Api';

const STATUS_FILTER_ALL = 'all' as const;
type StatusFilter = typeof STATUS_FILTER_ALL | ProductExampleStatus;

const statusChipColor = (status: ProductExampleStatus | undefined): 'success' | 'warning' | 'default' => {
    switch (status) {
        case ProductExampleStatus.Approved:
            return 'success';
        case ProductExampleStatus.Draft:
            return 'warning';
        case ProductExampleStatus.Deprecated:
        default:
            return 'default';
    }
};

/**
 * Lists product examples (formerly XML examples), grouped/filterable by
 * product (FileType) and status. The card is clickable; secondary actions
 * inline.
 */
const ProductExamplesList: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('productExamples.list.title');
    const context = useApplicationContext();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();

    const [items, setItems] = useState<ProductExampleSummaryDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(STATUS_FILTER_ALL);
    const [productFilter, setProductFilter] = useState<GetFileTypeData | null>(null);
    const [products, setProducts] = useState<GetFileTypeData[]>([]);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const fetchProducts = useCallback(async () => {
        try {
            const response = await context.api.fileType.fileTypeGetFileTypes();
            setProducts(response.data ?? []);
        } catch {
            // swallow — picker just stays empty
        }
    }, [context.api.fileType]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await context.api.productExample.productExampleList({
                status: statusFilter === STATUS_FILTER_ALL ? undefined : statusFilter,
                fileTypeId: productFilter?.id ?? undefined,
            });
            setItems(response.data ?? []);
        } catch {
            setError(t('productExamples.list.loadError'));
        } finally {
            setLoading(false);
        }
    }, [context.api.productExample, statusFilter, productFilter, t]);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);
    useEffect(() => { fetchData(); }, [fetchData]);

    const sortedItems = useMemo(
        () => [...items].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')),
        [items],
    );

    const handleDelete = async (id: string, title: string | undefined) => {
        if (!window.confirm(t('productExamples.list.confirmDelete', {title: title ?? '-'}))) return;
        setDeletingId(id);
        try {
            await context.api.productExample.productExampleDelete(id);
            enqueueSnackbar(t('productExamples.list.deleted'), {variant: 'success'});
            await fetchData();
        } catch {
            enqueueSnackbar(t('productExamples.list.deleteError'), {variant: 'error'});
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Box sx={{width: '100%', p: 2}}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                sx={{alignItems: {xs: 'stretch', sm: 'center'}, justifyContent: 'space-between', mb: 3}}
            >
                <Box>
                    <Typography variant="h4" component="h1">
                        {t('productExamples.list.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('productExamples.list.subtitle')}
                    </Typography>
                </Box>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} sx={{alignItems: {sm: 'center'}}}>
                    <Autocomplete<GetFileTypeData>
                        size="small"
                        sx={{minWidth: 220}}
                        options={products}
                        value={productFilter}
                        onChange={(_, value) => setProductFilter(value)}
                        getOptionLabel={(option) => option.name ?? ''}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => (
                            <TextField {...params} label={t('productExamples.list.productFilter')}/>
                        )}
                    />
                    <FormControl size="small" sx={{minWidth: 160}}>
                        <InputLabel id="product-examples-status-filter">
                            {t('productExamples.list.statusFilter')}
                        </InputLabel>
                        <Select
                            labelId="product-examples-status-filter"
                            label={t('productExamples.list.statusFilter')}
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                        >
                            <MenuItem value={STATUS_FILTER_ALL}>{t('productExamples.list.statusAll')}</MenuItem>
                            <MenuItem value={ProductExampleStatus.Draft}>{t('productExamples.status.draft')}</MenuItem>
                            <MenuItem value={ProductExampleStatus.Approved}>{t('productExamples.status.approved')}</MenuItem>
                            <MenuItem value={ProductExampleStatus.Deprecated}>{t('productExamples.status.deprecated')}</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title={t('productExamples.list.refresh')}>
                        <span>
                            <IconButton onClick={fetchData} disabled={loading}>
                                {loading ? <CircularProgress size={20}/> : <Refresh/>}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<Add/>}
                        onClick={() => navigate('/admin/product-examples/new')}
                    >
                        {t('productExamples.list.newButton')}
                    </Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            {loading && items.length === 0 && (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress/>
                </Box>
            )}

            {!loading && sortedItems.length === 0 && !error && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                            {t('productExamples.list.empty')}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <Grid container spacing={2}>
                {sortedItems.map(item => (
                    <Grid key={item.id} size={{xs: 12, sm: 6, md: 4, lg: 3}} sx={{display: 'flex'}}>
                        <Card variant="outlined" sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
                            <CardActionArea
                                onClick={() => item.id && navigate(`/admin/product-examples/${item.id}`)}
                                sx={{flex: 1, alignItems: 'stretch', display: 'flex'}}
                            >
                                <CardContent sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Stack direction="row" spacing={1} sx={{justifyContent: 'space-between', alignItems: 'flex-start'}}>
                                        <Typography variant="h6" sx={{lineHeight: 1.2, wordBreak: 'break-word', flex: 1}}>
                                            {item.title ?? '—'}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={t(`productExamples.status.${ProductExampleStatus[item.status ?? ProductExampleStatus.Draft].toLowerCase()}`)}
                                            color={statusChipColor(item.status)}
                                            sx={{flexShrink: 0, fontWeight: 600}}
                                        />
                                    </Stack>

                                    {item.fileTypeName && (
                                        <Chip
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            label={item.fileTypeName}
                                            sx={{alignSelf: 'flex-start'}}
                                        />
                                    )}

                                    {item.description && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                    )}

                                    {(item.features?.length ?? 0) > 0 && (
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5}}>
                                            {item.features!.slice(0, 4).map(f => (
                                                <Chip key={f} label={f} size="small" variant="outlined"/>
                                            ))}
                                            {(item.features!.length ?? 0) > 4 && (
                                                <Chip label={`+${item.features!.length - 4}`} size="small" variant="outlined"/>
                                            )}
                                        </Box>
                                    )}

                                    <Box sx={{flex: 1}}/>

                                    <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 0.5, mt: 1}}>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={t('productExamples.list.snippetCount', {count: item.snippetCount ?? 0})}
                                        />
                                        {item.targetVersion && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`${t('productExamples.fields.targetVersion')}: ${item.targetVersion}`}
                                            />
                                        )}
                                    </Stack>

                                    <Typography variant="caption" color="text.secondary">
                                        {t('productExamples.list.updated')}: {item.updatedAt ? dayjs(item.updatedAt).format('DD.MM.YYYY HH:mm') : '—'}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>

                            <Stack direction="row" spacing={0.5} sx={{px: 1, pb: 1, justifyContent: 'flex-end'}}>
                                <Tooltip title={t('productExamples.list.edit')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => item.id && navigate(`/admin/product-examples/${item.id}`)}
                                    >
                                        <Edit fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('productExamples.list.delete')}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            disabled={deletingId === item.id}
                                            onClick={() => item.id && handleDelete(item.id, item.title)}
                                        >
                                            {deletingId === item.id
                                                ? <CircularProgress size={16}/>
                                                : <Delete fontSize="small"/>}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default ProductExamplesList;
